import { DaySelector } from '@common/components/DaySelector';
import {
  ExperienceStepSchema,
  PackagesFormValues,
  PackageType,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { useGetSpaPackage } from '@features/spa';
import { useTranslations } from '@lang';
import moment from 'moment';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { SpaTitleWrapper } from '../../../components/ExperiencesGroup/ExperiencesGroup.styles';
import { usePackageConfiguration } from '../../../contexts';
import { PageTitle } from '../../FormSteps.styles';
import { useGroupOptionsByDateAndOptionGroup } from '../../hooks/useGroupOptionsByDate';
import { useHashNavigation } from '../../hooks/useHashNavigation';
import { useStepFooter } from '../../hooks/useStepFooter';

import { DayContainer } from '@features/DynamicPackagesConfigurationWizard/components/AvailabilityForm/AvailabilityForm.styles';
import { useExperienceSameTimes } from '../../../hooks/useExperienceSameTimes';
import { SpaSectionWrapper } from '../ExperiencesStep.styles';
import { useCheckSpaDayIsAvailable } from './hooks/useCheckSpaDayIsAvailable';
import { useDateGroupSelection } from './hooks/useDateGroupSelection';
import { MultipleOptionsExperienceTimeslotSelection } from './MultipleOptionsExperienceTimeslotSelection/MultipleOptionsExperienceTimeslotSelection';
import {
  ExperienceTitleWrapper,
  ExperienceWrapper,
  SpaEntitySelectionWrapper,
  SpaEntityStepWrapper,
} from './RepeatingExperiencesStep.styles';
import { SingleOptionExperienceTimeslotSelection } from './SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection';
import { SpaLabel } from './SingleOptionExperienceTimeslotSelection/SingleOptionExperienceTimeslotSelection.styles';
import type { ExperiencesByDay } from './types';

export const RepeatingExperiencesTimeslotsStep = () => {
  const {
    property,
    journeyParams,
    spaPackageId,
    stayDuration,
    totalGuestAmount,
    spaOpenTime,
    spaCloseTime,
    isRepeatedFlexibleEnabled,
  } = usePackageConfiguration();

  const numberOfGuests = totalGuestAmount;

  useHashNavigation({ hash: 'experiencesTimeslotsStep' });

  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');
  const guests = watch('guests') || [];

  const { experienceSameTimes, toggleExperienceSameTime, canEnableMultiGuestSameTime } =
    useExperienceSameTimes({
      selectedExperiences,
      numberOfGuests,
      defaultValue: true,
      queryClient,
      propertyId: Number(property.id),
      spaPackageId: Number(spaPackageId),
      spaOpenTime,
      spaCloseTime,
    });

  const { data: spaDay } = useGetSpaPackage(
    Number(property.id),
    Number(spaPackageId),
    property.defaultLanguage,
    true
  );

  const selectedExperiencesWithoutExtras = (selectedExperiences || []).filter(
    (exp) => exp.type !== 'spa-extras'
  );

  // Repeating packages: PackageType.REPEATING
  const selectedExperiencesByDay: ExperiencesByDay[] = useGroupOptionsByDateAndOptionGroup(
    moment(journeyParams.fromDate),
    selectedExperiencesWithoutExtras,
    PackageType.REPEATING,
    stayDuration,
    numberOfGuests,
    spaDay?.configurationGroups,
    isRepeatedFlexibleEnabled
  );

  // Get all possible dates across the stay
  const allPossibleDates = Array.from({ length: stayDuration + 1 }, (_, i) =>
    moment(journeyParams.fromDate).add(i, 'days').format('YYYY-MM-DD')
  );

  const { availableDates: spaAvailableDates } = useCheckSpaDayIsAvailable({
    propertyId: property.id,
    spaPackageId: Number(spaPackageId),
    datesToCheck: allPossibleDates,
    numberOfGuests,
    enabled: true,
  });

  const {
    dateGroupData,
    handleDayChange,
    handleGuestDayChange,
    clearGuestOverridesForGroup,
    getGuestDay,
    getGuestAvailableDates,
  } = useDateGroupSelection({
    selectedExperiencesByDay,
    journeyParams,
    guests,
    setValue,
    getValues,
    spaAvailableDates,
    isRepeatedFlexibleEnabled,
  });

  const handleToggleSameTime = useCallback(
    (experienceIndex: number, groupKey: string) => {
      const newValue = toggleExperienceSameTime(experienceIndex);
      if (newValue && isRepeatedFlexibleEnabled) {
        clearGuestOverridesForGroup(groupKey);
      }
    },
    [toggleExperienceSameTime, isRepeatedFlexibleEnabled, clearGuestOverridesForGroup]
  );

  const totalRequiredBookings = selectedExperiencesByDay.reduce((count, day) => {
    return (
      count +
      day.options.reduce((optCount, opt) => {
        if (!opt.options?.length) return optCount;
        const cap = opt.maximumSelections
          ? Math.min(opt.maximumSelections, opt.options.length)
          : opt.options.length;
        return optCount + cap;
      }, 0)
    );
  }, 0);

  const totalTimeSlotsRequired = totalRequiredBookings * numberOfGuests;

  const totalValidTimeslots = guests.reduce((total, guest) => {
    const experiences = guest.experiences || [];
    if (ExperienceStepSchema.safeParse(experiences).success) {
      const validExperiences = experiences.filter((b) => !!b && b.startTime);
      return total + validExperiences.length;
    }
    return total;
  }, 0);

  const isValid = totalValidTimeslots === totalTimeSlotsRequired;

  const footer = useStepFooter({ isValid });

  return (
    <>
      <ExperienceWrapper>
        <ExperienceTitleWrapper>
          <SpaTitleWrapper>
            <PageTitle>Spa</PageTitle>
          </SpaTitleWrapper>
          <h2 data-cy="page-title">{t('spa.package.packageAvailabilityTitle')}</h2>
        </ExperienceTitleWrapper>
        <SpaSectionWrapper>
          <SpaEntityStepWrapper>
            {dateGroupData.map((group) => {
              const { experiences, selectedDay, availableDates, groupKey } = group;
              const currentlySelectedDate = selectedDay
                ? moment(journeyParams.fromDate).add(selectedDay, 'days').format('YYYY-MM-DD')
                : availableDates[0];

              const anyExperienceIsSameTime =
                experiences.length === 0 ||
                experiences.some((exp) => experienceSameTimes[exp.experienceIndex] ?? false);
              const showSharedDayPicker = !isRepeatedFlexibleEnabled || anyExperienceIsSameTime;

              return (
                <div key={`date-group-${groupKey}`}>
                  {experiences.length > 0 && (
                    <>
                      {showSharedDayPicker && (
                        <DayContainer>
                          <DaySelector
                            availableDates={availableDates}
                            startDate={journeyParams.fromDate}
                            endDate={journeyParams.toDate || journeyParams.fromDate}
                            currentSelection={currentlySelectedDate}
                            onChange={(dateString) => handleDayChange(groupKey, dateString)}
                          />
                        </DayContainer>
                      )}

                      {experiences.map((experience) => {
                        const { key, type, data, experienceIndex } = experience;
                        const isSameTime = experienceSameTimes[experienceIndex] ?? false;
                        const optionIds =
                          type === 'group'
                            ? data.options.map((opt) => Number(opt.optionId))
                            : [Number(data.optionId)];
                        const canToggleSameTime = canEnableMultiGuestSameTime(
                          currentlySelectedDate,
                          optionIds
                        );

                        const showPerGuestDayPicker = isRepeatedFlexibleEnabled && !isSameTime;

                        const renderPerGuestDayPicker = (guestIndex: number) => {
                          if (!showPerGuestDayPicker) return null;
                          const guestDay = getGuestDay(groupKey, guestIndex);
                          const guestCurrentSelection =
                            guestDay !== undefined
                              ? moment(journeyParams.fromDate)
                                  .add(guestDay, 'days')
                                  .format('YYYY-MM-DD')
                              : currentlySelectedDate;
                          return (
                            <>
                              <SpaLabel required>{t('spa.selectDate')}</SpaLabel>
                              <DayContainer>
                                <DaySelector
                                  availableDates={getGuestAvailableDates(groupKey, guestIndex)}
                                  startDate={journeyParams.fromDate}
                                  endDate={journeyParams.toDate || journeyParams.fromDate}
                                  currentSelection={guestCurrentSelection}
                                  onChange={(dateString) =>
                                    handleGuestDayChange(groupKey, guestIndex, dateString)
                                  }
                                />
                              </DayContainer>
                            </>
                          );
                        };

                        if (type === 'group') {
                          return (
                            <SpaEntitySelectionWrapper key={key}>
                              {isSameTime ? (
                                <MultipleOptionsExperienceTimeslotSelection
                                  experiences={data}
                                  experienceIndex={experienceIndex}
                                  configurationGroups={spaDay?.configurationGroups || []}
                                  guestIndex={0}
                                  selectedDayNumber={selectedDay}
                                  numberOfGuests={numberOfGuests}
                                  isSameTimeForAllGuests={true}
                                  onToggleSameTime={
                                    canToggleSameTime
                                      ? () => handleToggleSameTime(experienceIndex, groupKey)
                                      : undefined
                                  }
                                />
                              ) : (
                                Array.from({ length: numberOfGuests }, (_, guestIndex) => (
                                  <MultipleOptionsExperienceTimeslotSelection
                                    key={`guest-${guestIndex}`}
                                    experiences={data}
                                    experienceIndex={experienceIndex}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={guestIndex}
                                    selectedDayNumber={
                                      getGuestDay(groupKey, guestIndex) ?? selectedDay
                                    }
                                    numberOfGuests={numberOfGuests}
                                    isSameTimeForAllGuests={false}
                                    onToggleSameTime={
                                      guestIndex === 0 && canToggleSameTime
                                        ? () => handleToggleSameTime(experienceIndex, groupKey)
                                        : undefined
                                    }
                                    dayPicker={renderPerGuestDayPicker(guestIndex)}
                                  />
                                ))
                              )}
                            </SpaEntitySelectionWrapper>
                          );
                        } else {
                          return (
                            <SpaEntitySelectionWrapper key={key}>
                              {isSameTime ? (
                                <SingleOptionExperienceTimeslotSelection
                                  experience={data}
                                  experienceIndex={experienceIndex}
                                  configurationGroups={spaDay?.configurationGroups || []}
                                  guestIndex={0}
                                  selectedDayNumber={selectedDay}
                                  numberOfGuests={numberOfGuests}
                                  isSameTimeForAllGuests={true}
                                  onToggleSameTime={
                                    canToggleSameTime
                                      ? () => handleToggleSameTime(experienceIndex, groupKey)
                                      : undefined
                                  }
                                />
                              ) : (
                                Array.from({ length: numberOfGuests }, (_, guestIndex) => (
                                  <SingleOptionExperienceTimeslotSelection
                                    key={`guest-${guestIndex}`}
                                    experience={data}
                                    experienceIndex={experienceIndex}
                                    configurationGroups={spaDay?.configurationGroups || []}
                                    guestIndex={guestIndex}
                                    selectedDayNumber={
                                      getGuestDay(groupKey, guestIndex) ?? selectedDay
                                    }
                                    numberOfGuests={numberOfGuests}
                                    isSameTimeForAllGuests={false}
                                    onToggleSameTime={
                                      guestIndex === 0 && canToggleSameTime
                                        ? () => handleToggleSameTime(experienceIndex, groupKey)
                                        : undefined
                                    }
                                    dayPicker={renderPerGuestDayPicker(guestIndex)}
                                  />
                                ))
                              )}
                            </SpaEntitySelectionWrapper>
                          );
                        }
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </SpaEntityStepWrapper>
        </SpaSectionWrapper>
      </ExperienceWrapper>
      {footer}
    </>
  );
};
